import React from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Paper,
  Typography,
  Grid,
  LinearProgress,
  Tooltip
} from '@mui/material'
import AssessmentIcon from '@mui/icons-material/Assessment'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import WarningIcon from '@mui/icons-material/Warning'
import styles from './QASummaryStats.module.scss'

const StatCard = ({ icon: Icon, iconClass, label, value, subtext, loading }) => {
  return (
    <Paper className={`${styles.statCard} ${loading ? styles.loadingOverlay : ''}`} elevation={0} role='group' aria-label={label}>
      <Box className={styles.statHeader}>
        <Box className={`${styles.statIcon} ${iconClass}`} aria-hidden='true'>
          <Icon />
        </Box>
        <Typography className={styles.statLabel}>{label}</Typography>
      </Box>
      <Typography className={styles.statValue}>{value}</Typography>
      {subtext && <Typography className={styles.statSubtext}>{subtext}</Typography>}
    </Paper>
  )
}

StatCard.propTypes = {
  icon: PropTypes.elementType.isRequired,
  iconClass: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  subtext: PropTypes.string,
  loading: PropTypes.bool
}

const BreakdownList = ({ title, items, type, loading }) => {
  if (!items || items.length === 0) {
    return (
      <Paper className={styles.breakdownSection} elevation={0}>
        <Typography className={styles.breakdownTitle}>{title}</Typography>
        <Typography className={styles.emptyState}>No data available</Typography>
      </Paper>
    )
  }

  const displayItems = items.slice(0, 5)

  return (
    <Paper className={`${styles.breakdownSection} ${loading ? styles.loadingOverlay : ''}`} elevation={0}>
      <Typography className={styles.breakdownTitle}>{title}</Typography>
      {displayItems.map((item, index) => {
        const itemName = item.campaignName || item.agentName || item.label || item.name || 'Unknown'
        const itemTotal = item.total || (item.failCount + item.passCount) || 1
        const rate = type === 'passRate'
          ? item.passRate || 0
          : itemTotal > 0 ? ((item.failCount / itemTotal) * 100) : 0

        return (
          <Box key={itemName + index} className={styles.breakdownItem}>
            <Box className={styles.breakdownLabel}>
              <Tooltip title={itemName} placement="top">
                <Typography className={styles.breakdownName}>
                  {itemName}
                </Typography>
              </Tooltip>
              <Typography className={`${styles.breakdownRate} ${type === 'passRate' ? styles.passRate : styles.failRate}`}>
                {rate.toFixed(1)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.min(rate, 100)}
              classes={{
                root: styles.progressBar,
                bar: `${styles.progressBarFill} ${type === 'passRate' ? styles.progressPass : styles.progressFail}`
              }}
            />
          </Box>
        )
      })}
    </Paper>
  )
}

BreakdownList.propTypes = {
  title: PropTypes.string.isRequired,
  items: PropTypes.array,
  type: PropTypes.oneOf(['passRate', 'failRate']).isRequired,
  loading: PropTypes.bool
}

const QASummaryStats = ({ statistics = {}, loading = false }) => {
  const {
    totalEvaluated = 0,
    passRate = 0,
    avgScore = 0,
    avgApplicable = 0,
    mostFailedCriterion = null,
    byCampaign = [],
    byCriteria = []
  } = statistics || {}

  const scoreDisplay = avgApplicable > 0
    ? `${((avgScore / avgApplicable) * 100).toFixed(1)}%`
    : '—'

  const topFailure = mostFailedCriterion
    ? mostFailedCriterion.replace(/([A-Z])/g, ' $1').trim()
    : '—'

  return (
    <Box className={styles.root}>
      {/* Main Statistics Cards */}
      <Box className={styles.statsRow}>
        <StatCard
          icon={AssessmentIcon}
          iconClass={styles.iconBlue}
          label="Total Evaluated"
          value={totalEvaluated.toLocaleString()}
          subtext="calls with QA evaluation"
          loading={loading}
        />
        <StatCard
          icon={CheckCircleIcon}
          iconClass={styles.iconGreen}
          label="Pass Rate"
          value={`${passRate.toFixed(1)}%`}
          subtext="calls meeting all criteria"
          loading={loading}
        />
        <StatCard
          icon={TrendingUpIcon}
          iconClass={styles.iconOrange}
          label="Avg Score"
          value={scoreDisplay}
          subtext="average criteria pass rate"
          loading={loading}
        />
        <StatCard
          icon={WarningIcon}
          iconClass={styles.iconRed}
          label="Top Failure"
          value={topFailure}
          subtext="most failed criterion"
          loading={loading}
        />
      </Box>

      {/* Breakdown Sections */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <BreakdownList
            title="Pass Rate by Campaign"
            items={byCampaign}
            type="passRate"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <BreakdownList
            title="Failure Rate by Criteria"
            items={byCriteria}
            type="failRate"
            loading={loading}
          />
        </Grid>
      </Grid>
    </Box>
  )
}

QASummaryStats.propTypes = {
  statistics: PropTypes.shape({
    totalEvaluated: PropTypes.number,
    passRate: PropTypes.number,
    avgScore: PropTypes.number,
    avgApplicable: PropTypes.number,
    mostFailedCriterion: PropTypes.string,
    byCampaign: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      passRate: PropTypes.number,
      total: PropTypes.number
    })),
    byAgent: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      passRate: PropTypes.number,
      total: PropTypes.number
    })),
    byCriteria: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      failCount: PropTypes.number,
      total: PropTypes.number
    }))
  }),
  loading: PropTypes.bool
}

export default QASummaryStats
